import qs from 'qs';
import { useQuery } from 'react-query';
import { network } from 'services/network';

import { QueryKeys } from 'api/queryKeys';

import { IAssignee } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/battlecards.types';

const queryFn = (params: QueryParams) => {
    const querystring = qs.stringify(params, { addQueryPrefix: true, skipNulls: true });

    const url = `api/bo/getRiskRegister${querystring}`;

    return network.get(url).then((response) => {
        return response.data;
    });
};

export const useQueryGetRiskRegister = (params: QueryParams) => {
    return useQuery<QueryFnResponse, QueryError, QueryResponse, [QueryKeys, unknown]>({
        queryKey: ['getRiskRegister', params],
        queryFn: () => queryFn(params),
        select: (data) => {
            return {
                riskList: data.risks.map((item) => {
                    const activitiesByKey: Risk['activitiesByKey'] = {
                        wbs: null,
                        activityCodes: null,
                        cluster: null,
                        specific: null,
                        segment: null,
                        all: null,
                    };

                    let linkOption: Risk['linkOption'] = 'all';
                    const wbs = item.segment.filter.filters.find((item) => item.name === 'WBS_SEARCH');
                    const activityCodes = item.segment.filter.filters.find((item) => item.name === 'ACTIVITY_CODES');
                    const cluster = item.segment.filter.filters.find((item) => item.name === 'CLUSTER_CARD_BOARD');
                    const specific = item.segment.filter.filters.find((item) => item.name === 'CUSTOM_ACTIVITIES_SET');
                    const segment = item.segment.userGenerated
                        ? { id: item.segment.segmentId, name: item.segment.name, url: item.segment.url }
                        : null;

                    if (segment) {
                        activitiesByKey.segment = segment;
                        linkOption = 'segment';
                    } else if (wbs) {
                        activitiesByKey.wbs = Number(wbs.data[0]);
                        linkOption = 'wbs';
                    } else if (activityCodes) {
                        activitiesByKey.activityCodes = activityCodes.data.map(Number);
                        linkOption = 'activityCodes';
                    } else if (cluster) {
                        activitiesByKey.cluster = Number(cluster.data[0]);
                        linkOption = 'cluster';
                    } else if (specific) {
                        activitiesByKey.specific = specific.data;
                        linkOption = 'specific';
                    }

                    return {
                        id: item.riskId,
                        name: item.title,
                        details: item.details,
                        status: item.status.name,
                        probability: item.probability.name,
                        impactTypeUnit: item.impactType.value,
                        impactType: item.impactType.key,
                        distribution: item.distribution.name,
                        durationVariance: {
                            min: item.distributionRange.min,
                            likely: item.distributionRange.middle,
                            max: item.distributionRange.max,
                        },
                        assignee: item.assignee,
                        linkedActivities: item.linkedActivities,
                        tags: item.tags,
                        linkOption: linkOption,
                        activitiesByKey: activitiesByKey,
                    };
                }),
                info: data.info,
            };
        },
        enabled: Boolean(params.metaDataId),
    });
};

interface QueryParams {
    contractId: number;
    metaDataId: number;
}

export interface Risk {
    id: number;
    name: string;
    details: string;
    status: 'NEW' | 'OPEN' | 'CLOSED';
    probability: 'VERY_LOW' | 'LOW' | 'MEDIUM' | 'HIGH' | 'VERY_HIGH' | 'CERTAIN';
    impactTypeUnit: '%' | 'days';
    impactType: 'ABSOLUTE' | 'RELATIVE';
    distribution: 'UNIFORM' | 'TRIANGLE' | 'BETA_PERT';
    durationVariance: {
        min: number;
        likely: number | null;
        max: number;
    };
    assignee: IAssignee;
    linkedActivities: number;
    tags: { name: string; id: number }[];
    linkOption: 'wbs' | 'activityCodes' | 'cluster' | 'specific' | 'segment' | 'all';
    activitiesByKey: {
        wbs: number | null;
        activityCodes: number[] | null;
        cluster: number | null;
        specific: string[] | null;
        segment: {
            id: number;
            name: string;
            url: string | null;
        } | null;
        all: null;
    };
}
export interface QueryResponse {
    riskList: Risk[];
    info: IToolTip;
}

interface QueryFnResponse {
    risks: {
        assignee: IAssignee;
        cardId: number;
        contract: { id: number; name: string };
        details: string;
        distribution: { id: number; name: Risk['distribution'] };
        distributionRange: { min: number; middle: number | null; max: number };
        impactType: { key: Risk['impactType']; value: Risk['impactTypeUnit'] };
        probability: { id: number; name: Risk['probability']; value: number };
        project: { id: number; name: string };
        riskId: number;
        riskUId: string;
        status: { id: number; name: Risk['status']; label: string };
        title: string;
        type: { id: number; name: 'RISK_EVENT'; label: 'Risk Event' };
        linkedActivities: number;
        tags: { name: string; id: number }[];
        segment: {
            segmentId: number;
            contractId: number;
            name: string;
            filter: {
                filters: {
                    name: string;
                    data: string[];
                    operation: null;
                }[];
            };
            entireProject: false;
            url: string | null;
            userGenerated: boolean;
        };
    }[];
    info: IToolTip;
}
